import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { PortalLayout } from '../components/PortalLayout';
import { PortalSidebar } from '../components/PortalSidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ScrollReveal } from '../components/ScrollReveal';
import { actChapters, actMeta, actSidebarGroups } from '../data/actContent';
import { penalties, penaltyNote } from '../data/penalties';
import { FileDown, ArrowRight } from 'lucide-react';

const DpdpaAct = () => (
  <>
    <SEO
      title="DPDPA Act 2023 Full Text — All 9 Chapters & 44 Sections Explained"
      description="Browse every chapter and section of the Digital Personal Data Protection Act, 2023 (DPDPA) — consent, data fiduciary obligations, Data Principal rights, penalties and the Data Protection Board, explained in plain English."
      path="/dpdpa-act"
    />
    <PortalLayout
      breadcrumb={<Breadcrumbs trail={[{ label: 'DPDPA Act, 2023' }]} />}
      sidebar={<PortalSidebar heading="DPDPA Act, 2023" headingHref="/dpdpa-act" groups={actSidebarGroups} />}
    >
      <div data-testid="dpdpa-act-page" className="max-w-3xl">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-saffron-600 mb-3">The Statute · Enacted 11 August 2023</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-navy leading-tight mb-4">The Digital Personal Data Protection Act, 2023</h1>
        <p className="text-gray-600 leading-relaxed mb-6">
          {actMeta.chapters} chapters, {actMeta.sections} sections and one Schedule — the full lifecycle of personal data, from who it applies to, to how it's enforced. Use the browser on the left to jump to any chapter or section.
        </p>
        <a
          href={actMeta.officialPdf}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="act-download-pdf-link"
          className="inline-flex items-center gap-2 text-sm text-navy hover:text-saffron-600 font-medium border border-gray-200 rounded-sm px-4 py-2 mb-12"
        >
          <FileDown size={14} /> Download official Act text (PDF)
        </a>

        <div className="space-y-14">
          {actChapters.map((ch) => (
            <ScrollReveal key={ch.number}>
              <div data-testid={`act-chapter-block-${ch.number}`}>
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-saffron-600 mb-1">{ch.chapter} · {ch.range}</p>
                <h2 className="font-serif text-2xl text-navy mb-2">{ch.title}</h2>
                <p className="text-gray-500 mb-5 leading-relaxed">{ch.description}</p>
                <div className="border-t border-gray-200">
                  {ch.sections.map((s) => (
                    <Link
                      key={s.id}
                      to={`/dpdpa-act/section/${s.id}`}
                      data-testid={`act-section-link-${s.id}`}
                      className="group flex items-center justify-between gap-4 py-3 border-b border-gray-200 hover:pl-2 transition-all duration-200"
                    >
                      <span className="flex gap-3 items-baseline">
                        <span className="font-mono text-xs text-gray-400 shrink-0">§{s.id}</span>
                        <span className="text-navy font-medium group-hover:text-saffron-600 transition-colors">{s.title}</span>
                      </span>
                      <ArrowRight size={14} className="text-gray-300 group-hover:text-saffron shrink-0 group-hover:translate-x-1 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <div className="bg-navy rounded-sm mt-16 p-8" data-testid="act-penalties-table">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-saffron mb-3">The Schedule · Section 33(1)</p>
          <h2 className="font-serif text-2xl text-white mb-6">Monetary penalties under the DPDPA</h2>
          <div className="border border-white/10 divide-y divide-white/10">
            {penalties.map((p) => (
              <div key={p.section} className="flex items-center justify-between gap-4 px-5 py-4">
                <div>
                  <p className="text-sm text-white/80">{p.provision}</p>
                  <p className="font-mono text-xs text-white/40 mt-1">{p.section}</p>
                </div>
                <p className="font-serif text-lg sm:text-xl text-saffron whitespace-nowrap">{p.amount}</p>
              </div>
            ))}
          </div>
          <p className="text-white/40 text-xs mt-5">{penaltyNote}</p>
        </div>
      </div>
    </PortalLayout>
  </>
);

export default DpdpaAct;
